import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Building2, Shield, CheckCircle, XCircle } from 'lucide-react'
import api from '@/admin/lib/api'
import { setAuthToken } from '@/shared/lib/auth-token'
import { useSession } from '@/admin/hooks/use-session'
import { Logo } from '@/admin/components/ui/logo'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { useTranslation } from 'react-i18next'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type { PartnershipInvitationPublic } from '@/admin/types/organisations'

import { toastApiError } from '@/shared/lib/api-error'
export function PartnershipInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: session, isLoading: sessionLoading } = useSession()
  const [invitation, setInvitation] = useState<PartnershipInvitationPublic | null>(null)
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()
  const [accepting, setAccepting] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchDetails() {
      try {
        const res = await api.get(
          `/organisations/partnership-invitations/details/?token=${token}`
        )
        setInvitation(res.data)
      } catch {
        setError('Unable to load invitation details. The link may be invalid.')
      } finally {
        setLoading(false)
      }
    }
    if (token) fetchDetails()
  }, [token])

  // The accept endpoint returns the just-established session so the partner
  // never has to re-authenticate. Persist the opaque token (the SPA is
  // header-authed, not cookie-authed) and the upstream tenant slug, so
  // `useSession` sees a live session and the app lands in the right tenant
  // without a sign-in or tenant-selection step. Mirrors sso-callback-page.
  //
  // The partner reached this invitation through the inviting tenant, so the
  // launchpad "pick a partner to enter" step is redundant. Pre-select the
  // partner context from the accept payload - the same two sessionStorage keys
  // `switchToPartner` sets - so `SmartDashboard` skips the launchpad and drops
  // them straight into the inviting tenant's partner workspace.
  async function establishSessionFromAccept(data: unknown) {
    const payload = data as {
      session_token?: string
      tenant_slug?: string
      upstream_organisation_uuid?: string
      downstream_organisation_uuid?: string
    }
    if (payload?.session_token) {
      setAuthToken(payload.session_token)
    }
    if (payload?.tenant_slug) {
      sessionStorage.setItem('tenantSlug', payload.tenant_slug)
    }
    if (payload?.upstream_organisation_uuid && payload?.downstream_organisation_uuid) {
      sessionStorage.setItem(
        'activeOrganisationUuid',
        payload.upstream_organisation_uuid,
      )
      sessionStorage.setItem(
        'activePartnerUuid',
        payload.downstream_organisation_uuid,
      )
    }
    await queryClient.invalidateQueries({ queryKey: ['session'] })
    await queryClient.invalidateQueries({ queryKey: ['org-context'] })
  }

  async function handleAcceptAuthenticated() {
    setAccepting(true)
    try {
      const res = await api.post(
        '/organisations/partnership-invitations/accept/',
        { token },
      )
      await establishSessionFromAccept(res.data)
      toast.success('Partnership accepted successfully!')
      navigate('/dashboard')
    } catch (err) {
      toastApiError(err, 'Failed to accept the invitation.');
    } finally {
      setAccepting(false)
    }
  }

  function handleSignInWithPassword() {
    const tenantSlug = invitation?.upstream_tenant_slug
    const loginBase = tenantSlug ? `/${tenantSlug}/login` : '/login'
    const params = new URLSearchParams()
    params.set('next', `/partnership-invite/${token}`)
    // The create-account form is not rendered on the existing-account path,
    // so fall back to the invited address rather than dropping the prefill.
    const currentEmail =
      (ref.current?.values?.email as string | undefined) || invitation?.admin_email
    if (currentEmail) params.set('email', currentEmail)
    navigate(`${loginBase}?${params.toString()}`)
  }

  const fields = useMemo<FormField[]>(() => [
    {
      type: 'email',
      name: 'email',
      label: 'Email',
      placeholder: 'you@company.com',
      value: invitation?.admin_email ?? '',
      required: 'Please enter a valid email address',
      autoComplete: 'email',
    },
    {
      type: 'password',
      name: 'password',
      label: 'Choose a password',
      placeholder: 'At least 8 characters',
      value: '',
      required: 'Password must be at least 8 characters',
      autoComplete: 'new-password',
    },
  ], [invitation?.admin_email])

  const { Form, ref } = useForm(
    {
      name: 'accept-partnership-form',
      // Suppress the default footer; the submit button is rendered as `Form`
      // children below so its `accepting` state stays live (children are fresh
      // every render, unlike a config `submit` fn which is snapshotted).
      submit: () => null,
      fields,
    },
    async (values) => {
      const email = String(values.email ?? '').trim()
      const password = String(values.password ?? '')

      if (password.length < 8) {
        toast.error('Password must be at least 8 characters')
        return
      }
      if (!termsAccepted) {
        toast.error(
          t('invite.acceptTermsError', {
            defaultValue: 'Please accept the Platform Service Terms to continue.',
          }),
        )
        return
      }

      setAccepting(true)
      try {
        const res = await api.post(
          '/organisations/partnership-invitations/accept/',
          {
            token,
            email,
            password,
            first_name: invitation?.admin_first_name || '',
            last_name: invitation?.admin_last_name || '',
          },
        )
        await establishSessionFromAccept(res.data)
        toast.success('Partnership accepted - welcome aboard!')
        navigate('/dashboard')
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'Failed to accept the invitation.'
        toast.error(message)
      } finally {
        setAccepting(false)
      }
    },
    [invitation, token, termsAccepted],
  )

  const isAuthenticated = session?.is_authenticated

  const renderBrandingHeader = () => {
    if (!invitation) return null
    return (
      <div className="flex items-center justify-center gap-3 mb-8">
        {invitation.upstream_logo_url ? (
          <img
            src={invitation.upstream_logo_url}
            alt={invitation.upstream_organisation_name}
            className="h-8 max-w-[180px] object-contain"
          />
        ) : (
          <>
            <Logo size="sm" />
            {invitation.upstream_organisation_name && (
              <>
                <span className="text-[var(--ink-3)] text-[11px]">&times;</span>
                <span className="text-[14px] text-[var(--ink)]">
                  {invitation.upstream_organisation_name}
                </span>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] px-4">
      <div className="w-full max-w-md rounded-[8px] border-[0.5px] border-[var(--rule-strong)] p-8">
        {loading || sessionLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <XCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-error)]" />
            <p className="text-[13px] text-[var(--ink)]">
              {error}
            </p>
          </div>
        ) : !invitation ? (
          <div className="text-center py-8">
            <p className="clara-small">Invitation not found.</p>
          </div>
        ) : invitation.accepted ? (
          <div className="text-center py-8">
            {renderBrandingHeader()}
            <CheckCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-ok)]" />
            <h2 className="mb-2 text-[15px] text-[var(--ink)]">
              Invitation Already Accepted
            </h2>
            <p className="clara-small">
              This partnership invitation has already been accepted.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="mt-6 cursor-pointer rounded-[4px] bg-[var(--green)] px-6 py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)]"
            >
              Go to Dashboard
            </Button>
          </div>
        ) : invitation.expired ? (
          <div className="text-center py-8">
            {renderBrandingHeader()}
            <XCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-error)]" />
            <h2 className="mb-2 text-[15px] text-[var(--ink)]">
              Invitation Expired
            </h2>
            <p className="clara-small">
              This partnership invitation has expired. Please ask the inviting organisation to send a new one.
            </p>
          </div>
        ) : (
          <>
            {renderBrandingHeader()}

            <div className="text-center mb-6">
              <Building2 className="mx-auto mb-4 h-8 w-8 text-[var(--green)]" />
              <h2 className="mb-1 text-[15px] text-[var(--ink)]">
                Partnership Invitation
              </h2>
              <p className="clara-small">
                You have been invited to partner with{' '}
                <span className="font-medium text-[var(--ink)]">
                  {invitation.upstream_organisation_name}
                </span>
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
                <p className="clara-label mb-1">Your Organisation</p>
                <p className="text-[13px] text-[var(--ink)]">
                  {invitation.organisation_name}
                </p>
              </div>

              <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <Shield className="h-3 w-3 text-[var(--ink-3)]" />
                  <p className="clara-label">Assigned Role</p>
                </div>
                <p className="text-[13px] text-[var(--ink)]">
                  {invitation.role_name}
                </p>
              </div>
            </div>

            {isAuthenticated ? (
              <Button
                onClick={handleAcceptAuthenticated}
                disabled={accepting}
                className="w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
              >
                {accepting && <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />}
                Accept Partnership
              </Button>
            ) : invitation.sign_in_required ? (
              // The create-account form below can only reject this invitee, so
              // offer sign-in instead; `next` returns them here authenticated.
              <div className="border-t-[0.5px] border-t-[var(--rule)] pt-4">
                <p className="clara-small text-center mb-4">
                  {t('invite.existingAccountHint', {
                    defaultValue:
                      'You already have an account. Sign in with your usual details and you will be taken straight into {{organisationName}}.',
                    organisationName: invitation.organisation_name,
                  })}
                </p>
                <Button
                  onClick={handleSignInWithPassword}
                  className="w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)]"
                >
                  {t('invite.acceptAndSignIn', {
                    defaultValue: 'Accept invite and sign in',
                  })}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border-t-[0.5px] border-t-[var(--rule)] pt-4">
                  <p className="clara-small text-center mb-4">
                    Create your account to get started.
                  </p>
                  <Form className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 mt-2">
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={termsAccepted}
                          onCheckedChange={(checked) => setTermsAccepted(!!checked)}
                          className="h-4 w-4 rounded-[2px] border accent-[var(--green)]"
                        />
                        <span className="text-[11px] text-[var(--ink-3)]">
                          {t('invite.agreeToTerms', { defaultValue: 'I agree to the' })}{' '}
                          <a
                            href="https://iwarranty.co/platformserviceterms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--green)] underline transition-colors"
                          >
                            {t('invite.platformServiceTerms', {
                              defaultValue: 'Platform Service Terms',
                            })}
                          </a>
                          <span
                            aria-hidden="true"
                            className="ml-[2px] text-[var(--status-error)]"
                          >
                            *
                          </span>
                        </span>
                      </Label>
                    </div>
                    <div className="col-span-2 mt-5">
                      <Button
                        type="submit"
                        disabled={accepting || !termsAccepted}
                        className="w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
                      >
                        {accepting && <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />}
                        Accept Partnership
                      </Button>
                    </div>
                  </Form>
                </div>

                <div className="text-center">
                  <Button
                    variant="link"
                    onClick={handleSignInWithPassword}
                    className="cursor-pointer text-[11px] text-[var(--green)] underline"
                  >
                    Already have an account? Sign in
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
